import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import { Card, Tabs, Pagination, Spinner, Select, TextField, AppProvider, Page, Checkbox, Button } from '@shopify/polaris';
import { ResourceTableHeaders, ResourceTableBody } from '@presentational';
import findIndex from 'lodash/findIndex';
import debounce from 'lodash/debounce';
import omit from 'lodash/omit';
import DatePicker from '@presentational/DatePicker';
import locales from '@utils/locales.json';

import axiosRails from '@utils/axios';
import AutocompleteSearch from "@presentational/AutocompleteSearch";

const propTypes = {
  tabs: PropTypes.array, // If provided Tab filtering is enabled. Manipulated with status and statusTabIndex. Requires id:, content:, value:
  users: PropTypes.array, // If list provided then select to filter with user_id will be used, commonly used with "Seller" name using user.seller vendor as label.
  searchName: PropTypes.string, // The name of the search input to send to the backend.
  searchPlaceholder: PropTypes.string,
  filterByCreation: PropTypes.bool,

  filterURL: PropTypes.string.isRequired, // Endpoint to submit the filter params
  paramsName: PropTypes.string.isRequired, // params name to send all params withing. i.e. filterParams which will nest filterParams.user_id, filterParams.search_t, etc.

  initialFilters: PropTypes.object.isRequired, // Initial value for filters
  initialRecords: PropTypes.array.isRequired, // Initial records list.
  initialTotalPages: PropTypes.number.isRequired, // Initial total of pages

  columnsInfo: PropTypes.array.isRequired, // Columns information to feed ResourceTable Headers/Body components
  itemURL: PropTypes.string, // List item url
  // resourceTableProps: PropTypes.shape(ResourceTableBody.propTypes),
  resourceTableProps: PropTypes.object,

  children: PropTypes.oneOfType([PropTypes.func, PropTypes.number, PropTypes.arrayOf(PropTypes.node),]), // Extra filters
  pageProps: PropTypes.object, // Polaris page component props

  bulkActions: PropTypes.func, // a function that returns an array
  loading: PropTypes.bool,
  legacy: PropTypes.bool, // Legacy filters works with GET on #index action. New way is separated POST only for filtering
};

const defaultProps = {
  users: [],
  tabs: [],
  filterByCreation: false,
  initialFilters: {
    // user_id: 0,
    // status: "completed"
    // statusTabIndex: 0
    // startDate
    // endDate
  },
  resourceTableProps: {},
  loading: false,
  statusParamName: "status", // when filtering via built-in tabs
  legacy: true, // temporally will default to true, will change default to false later
}




class FilterTableProvider extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      filterState: props.initialFilters,
      records: props.initialRecords,
      page: 1,
      totalPages: props.initialTotalPages,
      showDatepicker: false,
      selectedItems: [],
    }

    this.delayedPaginate = debounce(() => this.paginate('filter'), 1000);
  }

  handleSelectionChange = (itemIds) => {
    this.setState({ selectedItems: itemIds });
  }

  render() {
    const props = this.props;
    const { filterState, page, totalPages, records } = this.state;

    const loading = this.state.loading || props.loading;

    let parsedBulkActions = null;
    if (props.bulkActions) {
      parsedBulkActions = props.bulkActions(this.state.selectedItems, this.paginate);
    }

    const renderProps = {
      filterState,
      paginate: this.paginate,
      updateFilter: this.updateFilter,
      delayedPaginate: this.delayedPaginate,
    }

    const {
      columnsInfo,
      tabs,
      users,
      itemURL,
      pageProps,
      searchName } = props;


    // console.log("FTP state", this.state);
    return (
      <AppProvider i18n={locales}>
        <Page
          fullWidth
          {...pageProps}
        >
          <Card>
            {(props.children || users.length > 0 || props.filterByCreation || !!props.searchName) &&
              <Card.Section>
                {/* Custom Filters */}
                {props.children && props.children(renderProps)}

                <div className="row">
                  {/* Search Term */}
                  {!!searchName &&
                    <div className="col-12 col-md-6">
                      <TextField
                        disabled={loading}
                        id={searchName}
                        label={I18n.t('actions.search')}
                        value={filterState[searchName]}
                        onChange={(value, id) => this.updateFilter(value, id, 'delayed')}
                        placeholder={props.searchPlaceholder}
                      />
                    </div>
                  }

                  {/* Users/Sellers dropdown */}
                  {users.length > 0 &&
                    <div className="col-12 col-md-6">
                      <AutocompleteSearch
                        placeholder="Buscar por Seller..."
                        filterOptions={users}
                        label="Seller"
                        searchDelay={300}
                        onClearCallback={() => this.handleUserChange("")}
                        onSelectionCallback={(selectedOptions) => this.handleUserChange(selectedOptions[0])}
                      />
                    </div>
                  }
                </div>

                {/* ===========> Created At Filter <=========== */}
                {props.filterByCreation &&
                  <div className="row">
                    <div className="col-12 mt10">
                      <Checkbox
                        checked={this.state.showDatepicker}
                        label={I18n.t("actions.filter_by_creation")}
                        onChange={this.toggleDatepicker}
                      />
                    </div>
                    {this.state.showDatepicker &&
                      <div className="col-12 mt10">
                        <div className="row">
                          <div className="col-12 col-md-6">
                            <div className="mt15">
                              <DatePicker
                                name="date"
                                initialValues={{ start: filterState.start_date, end: filterState.end_date }}
                                onChange={this.setParamsDate}
                                allowRange
                              />
                            </div>
                          </div>
                          <div className="col-12 mt10">
                            <Button loading={loading}
                              onClick={this.paginate}
                              primary>
                              {I18n.t("actions.filter_by_date")}
                            </Button>
                          </div>
                        </div>
                      </div>
                    }
                  </div>
                }
              </Card.Section>
            }

            {tabs.length > 0 &&
              <Tabs
                tabs={props.tabs}
                selected={filterState.statusTabIndex}
                onSelect={this.onStatusChange}>
              </Tabs>
            }

            <ResourceTableHeaders
              columnsInfo={columnsInfo}
            />

            {/* Selection of bulk actions is done on FilterTableProvider component(this), but it only works if bulkActions params is set from parent */}
            <ResourceTableBody
              items={records}
              itemURL={itemURL}
              columnsInfo={columnsInfo}
              selectedItems={this.state.selectedItems}
              onSelectionChange={this.handleSelectionChange}
              bulkActions={parsedBulkActions}
              loading={loading}
              {...props.resourceTableProps}
            />

            <div className="p20 flex flexColumn justifyCenter alignItemsCenter">
              <Pagination
                hasPrevious={page > 1}
                onPrevious={() => { this.paginate('prev') }}
                hasNext={page < totalPages}
                onNext={() => { this.paginate('next') }}
              />
              <div className="mt5">
                {loading &&
                  <div className="ml10 inline">
                    <Spinner size="small" color="teal" />
                  </div>
                }
              </div>
            </div>
          </Card>
        </Page>
      </AppProvider>
    )
  };

  handleUserChange = async (user_id) => {
    this.setState((state) => ({ filterState: { ...state.filterState, user_id }, }), this.paginate);
  }

  onStatusChange = async (tabIndex) => {
    const tab = this.props.tabs[tabIndex];
    this.setState((state) => ({
      filterState: {
        ...state.filterState,
        [this.props.statusParamName]: tab.value, // default is "status", certain cases use a different param name
        statusTabIndex: tabIndex
      }
    }), this.paginate)
  }

  updateFilter = (value, name, trigger = 'none') => {
    this.setState((state) => ({
      filterState: {
        ...state.filterState,
        [name]: value
      }
    }),
      () => {
        switch (trigger) { // callback
          case 'filter': this.paginate(); break;
          case 'delayed': this.delayedPaginate(); break;
          default: // nanimo
        }
      }
    );
  }
  setParamsDate = (values) => {
    this.updateFilter(values.start, "start_date")
    this.updateFilter(values.end, "end_date")
    this.delayedPaginate()
  }

  toggleDatepicker = async (val) => {
    this.setState({ showDatepicker: val }, () => {
      // console.log(val);
      if (!val) {
        this.paginate();
      }
    })
  }

  paginate = async (action = 'filter') => {
    const { paramsName, filterURL, legacy } = this.props;
    // console.log("Querying");
    this.setState({ loading: true })
    let newPage = this.state.page;

    switch (action) {
      case "next":
        newPage += 1; break;
      case "prev":
        newPage -= 1; break;
      default: // Filter
    }
    try {
      let filterParams = this.state.filterState;
      if (!(this.props.filterByCreation && this.state.showDatepicker)) {
        // Exclude dates if not enabled
        filterParams = omit(filterParams, ['start_date', 'end_date'])
      };

      const params = {
        page: newPage,
        is_pagination: true,
        [paramsName]: Object.keys(filterParams).length == 0 ? null : filterParams,
      };

      let response;
      if (legacy) {
        console.log("FPARAMS", params)
        response = await axiosRails.get(filterURL, { params: params }); // send as url params
      }
      else {
        response = await axiosRails.post(filterURL, params);
      }

      const { data } = response;
      console.log("FTP, response", data);

      this.setState({
        loading: false,
        records: data.records,
        page: Number(data.page),
        totalPages: data.totalPages,
        selectedItems: [],
      })

    } catch (error) {
      alert("Ocurrio un error.");
      console.error(error);
      this.setState({ loading: false })
    }
  }

}

FilterTableProvider.propTypes = propTypes;
FilterTableProvider.defaultProps = defaultProps;

export default FilterTableProvider;
